"use client"

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react"
import { useField } from "@payloadcms/ui"
import type { UploadFieldClientComponent } from "payload"
import {
  ImagePlus,
  Loader2,
  Pencil,
  Search,
  Trash2,
  Upload as UploadIcon,
  X,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Upload field.
 *
 * Two modes controlled by `field.hasMany`:
 *   • single (default): one media id stored; button shows thumbnail
 *     or "Choisir un média"
 *   • hasMany: grid of thumbnails with reorder via click-to-remove
 *     and "Ajouter" button.
 *
 * Trigger actions:
 *   1. "Choisir un média" / "Ajouter"   → opens the picker dialog
 *      (async grid of existing Media, searchable)
 *   2. "Téléverser un nouveau fichier"  → posts multipart/form-data
 *      to /api/media and appends the created doc to the selection
 *   3. "Supprimer"                       → clears the value (single)
 *      or removes the chip (hasMany)
 *
 * Stays compatible with Payload's native upload field wire format:
 *   single  → stores the media document id as number | string
 *   hasMany → stores an array of ids
 *
 * Filter / permission guards:
 *   • `field.filterOptions` is NOT yet applied client-side — the REST
 *     call still goes through Payload access control, so server-side
 *     rules keep unauthorized docs out of the grid.
 *   • `field.relationTo` drives which Media collection to search.
 */

type MaybeMedia =
  | number
  | string
  | {
      id: number | string
      url?: string | null
      filename?: string | null
      alt?: string | null
      mimeType?: string | null
    }
  | null
  | undefined

type MediaDoc = {
  id: number | string
  url: string | null
  filename: string | null
  alt: string | null
  mimeType: string | null
  width?: number | null
  height?: number | null
}

function toMediaDoc(raw: Record<string, unknown>): MediaDoc {
  return {
    id: raw.id as number | string,
    url: (raw.url as string | null | undefined) ?? null,
    filename: (raw.filename as string | null | undefined) ?? null,
    alt: (raw.alt as string | null | undefined) ?? null,
    mimeType: (raw.mimeType as string | null | undefined) ?? null,
    width:
      typeof raw.width === "number"
        ? raw.width
        : null,
    height:
      typeof raw.height === "number"
        ? raw.height
        : null,
  }
}

export const ShadcnUploadField: UploadFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<
    MaybeMedia | MaybeMedia[]
  >({ path: path ?? fieldName })

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)
  const hasMany = Boolean((field as { hasMany?: boolean }).hasMany)
  const mediaSlug =
    ((field as { relationTo?: string }).relationTo as string | undefined) ??
    "media"

  // Current selected ids — in Payload's wire format the value may be
  // an id, a populated doc, or an array of either.
  const selectedIds = useMemo<Array<string | number>>(() => {
    if (value == null) return []
    const arr = hasMany ? (Array.isArray(value) ? value : []) : [value]
    return arr
      .map((v) => {
        if (v == null) return null
        if (typeof v === "string" || typeof v === "number") return v
        if (typeof v === "object" && "id" in v) return v.id
        return null
      })
      .filter((v): v is string | number => v != null)
  }, [value, hasMany])

  // id → doc cache so thumbnails render without refetching.
  const [docs, setDocs] = useState<Map<string | number, MediaDoc>>(new Map())

  // Fetch titles for preselected ids on mount + whenever they change.
  useEffect(() => {
    const missing = selectedIds.filter((id) => !docs.has(id))
    if (missing.length === 0) return
    let cancelled = false
    ;(async () => {
      const next = new Map(docs)
      await Promise.all(
        missing.map(async (id) => {
          try {
            const res = await fetch(
              `/api/${mediaSlug}/${encodeURIComponent(String(id))}?depth=0`,
              { credentials: "include" },
            )
            if (!res.ok) return
            const raw = (await res.json()) as Record<string, unknown>
            next.set(id, toMediaDoc(raw))
          } catch {
            /* silent */
          }
        }),
      )
      if (!cancelled) setDocs(next)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaSlug, selectedIds.join(",")])

  const writeValue = (ids: Array<string | number>) => {
    if (hasMany) {
      setValue(ids as unknown as MaybeMedia[])
    } else {
      setValue((ids[0] ?? null) as unknown as MaybeMedia)
    }
  }

  const handleRemove = (id: string | number) => {
    const next = selectedIds.filter((x) => x !== id)
    writeValue(next)
  }

  const handleAdd = (doc: MediaDoc) => {
    setDocs((prev) => new Map(prev).set(doc.id, doc))
    if (hasMany) {
      if (selectedIds.includes(doc.id)) return
      writeValue([...selectedIds, doc.id])
    } else {
      writeValue([doc.id])
    }
  }

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
    >
      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedIds.map((id) => {
            const d = docs.get(id)
            return (
              <div
                key={String(id)}
                className="group relative h-24 w-24 overflow-hidden rounded-xl border border-border bg-muted"
              >
                {d?.url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={d.url}
                    alt={d.alt ?? d.filename ?? "Média"}
                    width={96}
                    height={96}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ImagePlus className="size-6" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  aria-label="Retirer ce média"
                  className="absolute right-1 top-1 inline-flex size-6 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 shadow-sm transition-opacity hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100 focus-visible:opacity-100"
                >
                  <Trash2 className="size-3.5" />
                </button>
                {d?.filename ? (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                    <p className="truncate text-[10px] font-medium text-white">
                      {d.filename}
                    </p>
                  </div>
                ) : null}
              </div>
            )
          })}
          {hasMany ? (
            <UploadDialogTrigger
              mediaSlug={mediaSlug}
              alreadySelectedIds={selectedIds}
              onPick={handleAdd}
              triggerClassName="h-24 w-24"
              triggerChildren={
                <span className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  <ImagePlus className="size-5" />
                  <span>Ajouter</span>
                </span>
              }
            />
          ) : null}
        </div>
      ) : (
        <UploadDialogTrigger
          mediaSlug={mediaSlug}
          alreadySelectedIds={selectedIds}
          onPick={handleAdd}
        />
      )}
    </FieldShell>
  )
}

/**
 * Dialog trigger + media picker grid. Shared between the single and
 * hasMany modes so the "empty state" button and the "+ Ajouter" tile
 * both open the same picker.
 */
function UploadDialogTrigger({
  mediaSlug,
  alreadySelectedIds,
  onPick,
  triggerClassName,
  triggerChildren,
}: {
  mediaSlug: string
  alreadySelectedIds: Array<string | number>
  onPick: (doc: MediaDoc) => void
  triggerClassName?: string
  triggerChildren?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerChildren ? (
          <button type="button" className={triggerClassName}>
            {triggerChildren}
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="group h-11 gap-2 rounded-xl"
          >
            <ImagePlus className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span>Choisir un média</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Bibliothèque média</DialogTitle>
        </DialogHeader>
        <MediaPicker
          mediaSlug={mediaSlug}
          alreadySelectedIds={alreadySelectedIds}
          onPick={(d) => {
            onPick(d)
            setOpen(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

function MediaPicker({
  mediaSlug,
  alreadySelectedIds,
  onPick,
}: {
  mediaSlug: string
  alreadySelectedIds: Array<string | number>
  onPick: (doc: MediaDoc) => void
}) {
  const [query, setQuery] = useState("")
  const [docs, setDocs] = useState<MediaDoc[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const fetchDocs = async (q: string) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        depth: "0",
        limit: "48",
        sort: "-createdAt",
      })
      if (q.trim()) {
        params.append("where[or][0][filename][like]", q.trim())
        params.append("where[or][1][alt][like]", q.trim())
      }
      const res = await fetch(`/api/${mediaSlug}?${params.toString()}`, {
        credentials: "include",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as {
        docs?: Array<Record<string, unknown>>
      }
      setDocs((data.docs ?? []).map(toMediaDoc))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(() => {
      void fetchDocs(query)
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const upload = async (file: File) => {
    setUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append("file", file)
      // Payload's REST upload endpoint expects the file as multipart
      // with optional sidecar JSON for the collection fields.
      const res = await fetch(`/api/${mediaSlug}`, {
        method: "POST",
        body: form,
        credentials: "include",
      })
      if (!res.ok) {
        const body = await res.text()
        throw new Error(
          `Upload refusé (${res.status}): ${body.slice(0, 200)}`,
        )
      }
      const json = (await res.json()) as { doc?: Record<string, unknown> }
      if (json.doc) {
        const doc = toMediaDoc(json.doc)
        setDocs((prev) => [doc, ...prev])
        onPick(doc)
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setUploading(false)
    }
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) void upload(file)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) void upload(file)
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Search + upload controls */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher dans la bibliothèque…"
            className="h-9 rounded-xl pl-8"
          />
        </div>
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-9 gap-2 rounded-xl"
        >
          {uploading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <UploadIcon className="size-3.5" />
          )}
          <span>Téléverser</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="sr-only"
          onChange={handleFileInput}
        />
      </div>

      {/* Drop zone */}
      <div
        ref={dropRef}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          "rounded-xl border-2 border-dashed px-4 py-3 text-center text-xs transition-colors",
          dragOver
            ? "border-primary bg-primary/5 text-primary"
            : "border-border text-muted-foreground",
        )}
      >
        Glissez-déposez un fichier ici pour l&apos;importer, ou utilisez
        le bouton «&nbsp;Téléverser&nbsp;» ci-dessus.
      </div>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          <span>Chargement…</span>
        </div>
      ) : docs.length === 0 ? (
        <div className="py-10 text-center text-xs text-muted-foreground">
          Aucun média trouvé.
        </div>
      ) : (
        <div className="grid max-h-[420px] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4 md:grid-cols-6">
          {docs.map((d) => {
            const already = alreadySelectedIds.includes(d.id)
            return (
              <button
                key={String(d.id)}
                type="button"
                onClick={() => onPick(d)}
                disabled={already}
                className={cn(
                  "group relative aspect-square overflow-hidden rounded-xl border bg-muted transition-[border-color,transform]",
                  already
                    ? "border-primary opacity-60"
                    : "border-border hover:border-primary/60 hover:-translate-y-0.5",
                )}
              >
                {d.url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={d.url}
                    alt={d.alt ?? d.filename ?? "Média"}
                    width={120}
                    height={120}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ImagePlus className="size-6" />
                  </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                  <p className="truncate text-[10px] font-medium text-white">
                    {d.filename ?? `#${d.id}`}
                  </p>
                </div>
                {already ? (
                  <span className="absolute right-1 top-1 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary-foreground">
                    Sélectionné
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Intentionally exported so the importMap generator picks it up.
export { MediaPicker as _MediaPicker, UploadDialogTrigger as _UploadDialogTrigger }

export default ShadcnUploadField
